import styled, { keyframes } from 'styled-components/macro';
import scaleSize from 'utils/scale-size';
import { Focusable, FocusableParent } from 'components/navigation';
import { ReactComponent as QRSVG } from '../../assets/img/qr.svg';

export const StyledModalContainer = styled.div`
  width: ${scaleSize(1280)}px;
  height: ${scaleSize(720)}px;
  margin: ${scaleSize(180)}px auto;
  border-radius: ${scaleSize(24)}px;
  background-color: ${({ theme }) => theme.backgroundColor};
`;

export const StyledTitle = styled.div`
  padding-top: ${scaleSize(100)}px;
  padding-left: ${scaleSize(100)}px;
  padding-right: ${scaleSize(100)}px;
  min-height: ${scaleSize(280)}px;
  margin-bottom: ${scaleSize(20)}px;
  font-size: ${scaleSize(48)}px;
  line-height: ${scaleSize(1.5, false)};
  letter-spacing: -0.33px;
  color: ${({ theme }) => theme.defaultColor};
  text-align: center;
`;

export const StyledDescription = styled.div`
  height: ${scaleSize(96)}px;
  font-size: ${scaleSize(34)}px;
  line-height: 1.5;
  letter-spacing: normal;
  text-align: center;
  color: ${({ theme }) => theme.iconDefaultColor};
`;

export const StyledExitDescription = styled.div`
  width: ${scaleSize(580)}px;
  font-size: ${scaleSize(34)}px;
  line-height: 1.5;
  letter-spacing: normal;
  text-align: left;
  color: ${({ theme }) => theme.iconDefaultColor};
  padding-bottom: ${scaleSize(60)}px;
`;

export const StyledPendingDescription = styled.div`
  font-size: ${scaleSize(34)}px;
  line-height: ${scaleSize(48)}px;
  margin-top: ${scaleSize(254)}px;
  margin-bottom: ${scaleSize(40)}px;
  text-align: center;
`;

export const StyledLeftContent = styled.div`
  width: 60%;
  float: left;
`;

export const StyledRightContent = styled.div`
  width: 40%;
  float: right;
  text-align: right;
`;

export const StyledWhiteText = styled.span`
  color: ${({ theme }) => theme.defaultColor};
`;

export const StyledFocusableActionsWrapper = styled(FocusableParent)`
  height: ${scaleSize(360)}px;
  text-align: center;
`;

export const FocusableActionsWrapper = styled(FocusableParent)`
  margin-top: ${scaleSize(164)}px;
  text-align: center;
`;

export const StyledButtonWrapper = styled.div`
  display: inline-block;
  vertical-align: middle;
  margin-bottom: ${scaleSize(20)}px;
`;

export const StyledButtonText = styled.div`
  width: ${scaleSize(618)}px;
  height: ${scaleSize(80)}px;
`;

export const StyledSmallButtonText = styled.div`
  width: ${scaleSize(420)}px;
  height: ${scaleSize(80)}px;
`;

export const StyledInputWrapper = styled.div`
  margin-bottom: ${scaleSize(20)}px;
`;

export const StyledBlock = styled.div`
  width: ${scaleSize(680)}px;
  margin: ${scaleSize(34)}px auto;
`;

export const StyledBlockWrapper = styled.div`
  padding: ${scaleSize(80)}px ${scaleSize(120)}px;
`;

export const StyledModalTitle = styled.div`
  height: ${scaleSize(72)}px;
  font-size: ${scaleSize(48)}px;
  line-height: ${scaleSize(1.5, false)};
  letter-spacing: -0.33px;
  color: ${({ theme }) => theme.defaultColor};
  text-align: center;
  padding-top: ${scaleSize(100)}px;
  padding-left: ${scaleSize(200)}px;
  padding-right: ${scaleSize(200)}px;
`;

export const StyledExitTitle = styled.div`
  height: ${scaleSize(72)}px;
  padding-bottom: ${scaleSize(40)}px;
  font-size: ${scaleSize(48)}px;
  line-height: ${scaleSize(1.5, false)};
  letter-spacing: -0.33px;
  color: ${({ theme }) => theme.defaultColor};
`;

const cursorAnimation = keyframes`
  0% {opacity: 0;}
  50% {opacity: 1;}
  100% {opacity: 0;}
`;

export const StyledInputCursor = styled.div`
  display: inline-block;
  width: 2px;
  height: ${scaleSize(50)}px;
  vertical-align: middle;
  margin-top: -${scaleSize(4)}px;
  margin-left: -1px;
  background: ${({ theme }) => theme.mainTextColor};
  animation-name: ${cursorAnimation};
  animation-duration: 1s;
  animation-iteration-count: infinite;
`;

const errorAnimation = keyframes`
    0% { transform: translate(30px); }
    20% { transform: translate(-30px); }
    40% { transform: translate(15px); }
    60% { transform: translate(-15px); }
    80% { transform: translate(8px); }
    100% {transform: translate(0px); }
`;

export const StyledInput = styled(Focusable)`
  display: block;
  width: ${scaleSize(672)}px;
  height: ${scaleSize(88)}px;
  border-radius: ${scaleSize(12)}px;
  border-style: solid;
  border-width: ${scaleSize(4)}px;
  background-color: ${({ theme }) => theme.submenuActiveColor};
  overflow: hidden;
  animation-name: ${({ isValid }) => (isValid ? '' : errorAnimation)};
  animation-duration: 0.4s;
  animation-iteration-count: 1;

  border-color: ${({ isValid, theme }) => (!isValid ? theme.invalidInput : 'transparent')};

  &:focus {
    border-color: ${({ theme }) => theme.activeColor};
  }
`;

export const StyledInputInvalidMessage = styled.div`
  height: ${scaleSize(40)}px;
  margin-top: ${scaleSize(12)}px;
  font-size: ${scaleSize(32)}px;
  text-align: left;
  font-family: 'RobotoRegular', sans-serif;
  line-height: 1.25;
  letter-spacing: -0.22px;
  color: ${({ theme }) => theme.invalidInput};
`;

export const StyledInputText = styled.div`
  height: ${scaleSize(48)}px;
  min-width: ${scaleSize(600)}px;
  float: right;
  margin: 0 ${scaleSize(36)}px;
  text-align: center;
  white-space: nowrap;
  font-size: ${scaleSize(40)}px;
  line-height: ${scaleSize(88)}px;
  letter-spacing: ${scaleSize(-0.28)}px;
  color: ${({ isActive, theme }) => theme[isActive ? 'defaultColor' : 'iconDefaultColor']};
`;

export const QRIcon = styled(QRSVG)`
  width: ${scaleSize(320)}px;
  height: ${scaleSize(320)}px;
`;

export const StyledPinInputChar = styled.span`
  display: inline-block;
  width: ${scaleSize(45)}px;
  text-align: center;
  color: ${({ isActive, isPlaceholder, theme }) =>
    isActive ? theme.activeColor : isPlaceholder ? theme.iconDefaultColor : 'inherit'};
`;

export const StyledLoaderWrapper = styled.div`
  width: 100%;
  height: ${scaleSize(354)}px;
  position: absolute;
  right: 0;
  left: 0;
  margin: auto;
`;
